// Cost Recipe Library — future-proof formula catalogue for the Cost Sheet.
//
// Every recipe describes:
//   • `key`     — matches the operations_enabled key on the part (e.g.
//                 "cutting", "welding", "cnc"). Also serves as the storage
//                 field name inside `cost_sheet.lines`.
//   • `label`   — human title shown on the section card.
//   • `aliases` — extra keys/names to auto-detect this recipe from
//                 op_code / name (case-insensitive substring match).
//   • `inputs`  — array of { key, label, type, hint?, auto? } fields.
//                 `auto:true` means the value is prefilled from the part
//                 snapshot and non-editable unless the user overrides.
//   • `compute` — pure function taking inputs → { …derived, cost }.
//                 Every recipe MUST emit a `cost` key so totals sum up.
//
// To add a new operation later (e.g. Laser Cutting, Buffing, Grinding),
// just append another entry — no changes needed anywhere else.

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

// Helper — pick MHR from an ordered array of `{max_size, rate}` slabs.
// Returns the rate of the first slab where `size < max_size` (exclusive
// upper bound — a value equal to a boundary rolls into the NEXT bucket
// so "500-750 → ₹X" applies only to sizes strictly less than 750). Falls
// back to the largest slab when the size exceeds all boundaries.
export function pickSlabRate(slabs, size) {
  const s = num(size);
  const list = (slabs || []).slice().sort((a, b) => num(a.max_size) - num(b.max_size));
  if (list.length === 0) return { rate: 0, matched: null };
  const hit = list.find(sl => s < num(sl.max_size));
  if (hit) return { rate: num(hit.rate), matched: hit };
  return { rate: num(list[list.length - 1].rate), matched: list[list.length - 1] };
}

const rateXWeight = (inputs, ctx, rateKey = "rate", weightKey = "weight") => {
  const rate = num(inputs[rateKey]);
  // Fall back to the part's finished weight when the user hasn't set an
  // explicit weight for this section. Passed via ctx from the Cost Sheet.
  const explicit = num(inputs[weightKey]);
  const weight = explicit > 0 ? explicit : num(ctx?.finished_weight);
  return { cost: rate * weight, weight_used: weight };
};

// Recipe for a "machine hour rate" operation (CNC, VMC, …).
// Formula shown to the user: Rate ÷ Effective-Output.
const machineHourCompute = (i) => {
  const rate = num(i.machine_hour_rate);
  const cycle = num(i.cycle_time);
  const eff = num(i.efficiency);
  const output_per_hour = cycle > 0 ? 60 / cycle : 0;
  const effective_output = output_per_hour * (eff / 100);
  const cost = effective_output > 0 ? rate / effective_output : 0;
  return { output_per_hour, effective_output, cost };
};

// Raw Material types supported by the Cost Sheet's advanced RM section.
// Each carries the fields the user needs to enter to derive a gross weight.
//
// NOTE on units: the user's formulas assume density in g/cm³ (7.85 for MS),
// but the app has migrated to storing density as kg/cm³ (0.00785 for MS).
// The grossWeight helpers therefore multiply the incoming density by 1000
// before plugging into the divisor — that preserves the exact formulas the
// user specified in the spec doc while working with our current data.
export const RM_TYPES = [
  { code: "Rod",   label: "Rod",
    inputs: [
      { key: "od",     label: "OD (mm)",         type: "number" },
      { key: "length", label: "Length (mm)",     type: "number" },
    ],
    // Gross Weight = π × OD² × L × ρ_g_cm3 / 4,000,000
    grossWeight: (i, density) => (Math.PI * num(i.od) ** 2 * num(i.length) * (num(density) * 1000)) / 4_000_000,
  },
  { code: "Pipe",  label: "Pipe",
    inputs: [
      { key: "od",     label: "OD (mm)",         type: "number" },
      { key: "id",     label: "ID (mm)",         type: "number" },
      { key: "length", label: "Length (mm)",     type: "number" },
    ],
    // Gross Weight = π × (OD² − ID²) × L × ρ_g_cm3 / 4,000,000
    grossWeight: (i, density) => (Math.PI * (num(i.od) ** 2 - num(i.id) ** 2) * num(i.length) * (num(density) * 1000)) / 4_000_000,
  },
  { code: "Sheet", label: "Sheet",
    inputs: [
      { key: "width",     label: "Width (mm)",     type: "number" },
      { key: "length",    label: "Length (mm)",    type: "number" },
      { key: "thickness", label: "Thickness (mm)", type: "number" },
    ],
    // Gross Weight = W × L × T × ρ_g_cm3 / 1,000,000
    grossWeight: (i, density) => (num(i.width) * num(i.length) * num(i.thickness) * (num(density) * 1000)) / 1_000_000,
  },
  { code: "Strip", label: "Strip",
    inputs: [
      { key: "width",     label: "Width (mm)",     type: "number" },
      { key: "length",    label: "Length (mm)",    type: "number" },
      { key: "thickness", label: "Thickness (mm)", type: "number" },
    ],
    grossWeight: (i, density) => (num(i.width) * num(i.length) * num(i.thickness) * (num(density) * 1000)) / 1_000_000,
  },
];

// Cutting type registry used by the advanced Cutting section on the Cost Sheet.
export const CUTTING_TYPES_CS = [
  { code: "laser",     label: "Laser Cutting" },
  { code: "water_jet", label: "Water Jet" },
  { code: "shearing",  label: "Shearing" },
  { code: "bandsaw",   label: "Bandsaw Cutting" },
];

export const COST_RECIPES = [
  {
    key: "raw_material",
    label: "Raw Material",
    aliases: ["rm", "raw_material", "incoming_store"],
    optional: true,           // shows a checkbox at the top of the section
    isRawMaterialAdvanced: true, // Cost Sheet renders a bespoke UI for this
    compute(i) {
      // `density` is auto-fetched from BOM Master. Gross Weight is derived
      // from the chosen RM Type's formula. Scrap = (GW − FW) × recovery.
      const rmType = RM_TYPES.find(t => t.code === (i.rm_type || ""));
      const density = num(i.density);
      const gross_weight = rmType ? rmType.grossWeight(i, density) : num(i.gross_weight);
      const rm_rate = num(i.rm_rate);
      const gross_rm_value = gross_weight * rm_rate;
      const finish_weight = num(i.finish_weight);
      const scrap_weight = Math.max(0, gross_weight - finish_weight);
      const recovery = num(i.scrap_recovery_pct || 95) / 100;
      const scrap_rate = num(i.scrap_rate);
      const scrap_value = scrap_weight * recovery * scrap_rate;
      const cost = gross_rm_value - scrap_value; // Net RM Value
      return { gross_weight, gross_rm_value, scrap_weight, scrap_value, cost };
    },
  },

  {
    key: "cutting",
    label: "Cutting",
    aliases: ["cutting"],
    isCuttingAdvanced: true,     // Cost Sheet renders a bespoke UI for this
    compute(i, ctx) {
      // `ctx` carries auto-fetched Process Master rates for cutting + RM
      // section snapshot so bandsaw / shearing can pull dimensions & GW.
      const t = i.cutting_type || ctx?.process?.cutting_type || "";
      const p = ctx?.process || {};
      const rm = ctx?.rmSection || {};
      // Default rates when neither user nor Process Master have a value.
      const DEFAULTS = { laser: 8, water_jet: 20, shearing: 2 };
      if (t === "laser" || t === "water_jet") {
        const rate = num(i.rate_per_mm_thickness ?? p.cutting_rate_per_mm_thickness ?? DEFAULTS[t]);
        const path = num(i.cutting_path);
        const thickness = num(i.thickness);
        return { cost: rate * path * thickness };
      }
      if (t === "shearing") {
        const rate = num(i.rate_per_kg ?? p.cutting_rate_per_kg ?? DEFAULTS.shearing);
        const gw = num(i.gross_weight ?? ctx?.gross_weight);
        return { cost: rate * gw };
      }
      if (t === "bandsaw") {
        // Intelligent bandsaw — derive area + slab from the RM section
        // (Rod/Pipe via OD, Sheet/Strip via W×T). User overrides win.
        const rmType = String(rm.rm_type || "").toLowerCase();
        let autoArea = null, autoSlab = null;
        if (rmType === "rod" || rmType === "pipe") {
          const od = num(rm.od);
          if (od > 0) {
            autoArea = 0.0013 * od * od;
            autoSlab = od <= 100 ? "upto_100" : (od <= 200 ? "100_200" : "above_200");
          }
        } else if (rmType === "sheet" || rmType === "strip") {
          const w = num(rm.width), tk = num(rm.thickness);
          if (w > 0 && tk > 0) {
            autoArea = 0.00155 * w * tk;
            autoSlab = w <= 100 ? "upto_100" : (w <= 200 ? "100_200" : "above_200");
          }
        }
        const slabKey = i.applicable_range || autoSlab || "";
        // Hard-coded business defaults — Process Master overrides these
        // when configured, otherwise we always fall back to ₹1 / ₹1.5 / ₹2.
        const DEFAULT_SLAB_RATES = { upto_100: 1, "100_200": 1.5, above_200: 2 };
        const slabRates = {
          upto_100: num(p.bandsaw_rate_upto_100) || DEFAULT_SLAB_RATES.upto_100,
          "100_200": num(p.bandsaw_rate_100_200) || DEFAULT_SLAB_RATES["100_200"],
          above_200: num(p.bandsaw_rate_above_200) || DEFAULT_SLAB_RATES.above_200,
        };
        const chosenRate = slabRates[slabKey] || 0;
        const area = i.cutting_area !== "" && i.cutting_area != null
          ? num(i.cutting_area)
          : (autoArea ?? 0);
        return { cost: chosenRate * area, chosen_slab: slabKey, chosen_rate: chosenRate, auto_area: autoArea };
      }
      return { cost: 0 };
    },
  },

  {
    key: "machining",
    label: "Machining",
    aliases: ["machining"],
    isMachiningAdvanced: true,
    compute(i, ctx) {
      const cfg = ctx?.machiningConfig || {};
      const rm  = ctx?.rmSection || {};

      // Auto-fill dimensions from Raw Material section when the user
      // hasn't typed a value on the machining card:
      //   Rod / Pipe → RM's OD
      //   Sheet      → the LARGER of RM's width & length (single-dim
      //                inputs like CNC OD / VMC Diameter). For the
      //                VMC W×L mode, we set width = min, length = max
      //                to mirror natural width-length ordering.
      const rmType = String(rm.rm_type || "").toLowerCase();
      const isRodLike  = rmType === "rod" || rmType === "pipe";
      const isSheetLike = rmType === "sheet";
      const rmOd = num(rm.od);
      const rmW  = num(rm.width);
      const rmL  = num(rm.length);
      const rmBigger  = Math.max(rmW, rmL);
      const rmSmaller = Math.min(rmW, rmL);
      const autoSingleDim = isRodLike  ? rmOd
                          : isSheetLike ? rmBigger
                          : 0;

      // ── CNC leg ──
      const cncEnabled = i.cnc_enabled ?? true;
      const cncTyped = num(i.cnc_od);
      const cncSize = cncTyped > 0 ? cncTyped : autoSingleDim;
      const cncEff = num(i.cnc_efficiency ?? 75);
      const cncCycle = num(i.cnc_cycle_time);
      const cnc = { size: cncSize, mhr: 0, output_per_hour: 0, cost: 0, matched_slab: null, auto_size: cncTyped > 0 ? 0 : autoSingleDim };
      if (cncEnabled) {
        const picked = pickSlabRate(cfg.cnc_slabs, cncSize);
        cnc.mhr = picked.rate;
        cnc.matched_slab = picked.matched;
        cnc.output_per_hour = cncCycle > 0 ? (60 / cncCycle) * (cncEff / 100) : 0;
        cnc.cost = cnc.output_per_hour > 0 ? cnc.mhr / cnc.output_per_hour : 0;
      }

      // ── VMC leg ──
      const vmcEnabled = i.vmc_enabled ?? true;
      const vmcMode = i.vmc_size_mode || "diameter";
      let vmcSize;
      if (vmcMode === "wl") {
        const typedW = num(i.vmc_width);
        const typedL = num(i.vmc_length);
        // Empty W/L → seed from RM (sheet: smaller = width, larger = length)
        const wl_w = typedW > 0 ? typedW : (isSheetLike ? rmSmaller : 0);
        const wl_l = typedL > 0 ? typedL : (isSheetLike ? rmBigger  : 0);
        vmcSize = Math.max(wl_w, wl_l);
      } else {
        const typed = num(i.vmc_diameter);
        vmcSize = typed > 0 ? typed : autoSingleDim;
      }
      const vmcEff = num(i.vmc_efficiency ?? 75);
      const vmcCycle = num(i.vmc_cycle_time);
      const vmc = { size: vmcSize, mhr: 0, output_per_hour: 0, cost: 0, matched_slab: null,
                    auto_diameter: (vmcMode === "diameter" && num(i.vmc_diameter) === 0) ? autoSingleDim : 0,
                    auto_width:    (vmcMode === "wl" && num(i.vmc_width) === 0 && isSheetLike) ? rmSmaller : 0,
                    auto_length:   (vmcMode === "wl" && num(i.vmc_length) === 0 && isSheetLike) ? rmBigger  : 0 };
      if (vmcEnabled) {
        const picked = pickSlabRate(cfg.vmc_slabs, vmcSize);
        vmc.mhr = picked.rate;
        vmc.matched_slab = picked.matched;
        vmc.output_per_hour = vmcCycle > 0 ? (60 / vmcCycle) * (vmcEff / 100) : 0;
        vmc.cost = vmc.output_per_hour > 0 ? vmc.mhr / vmc.output_per_hour : 0;
      }

      const cost = (cnc.cost || 0) + (vmc.cost || 0);
      return { cnc, vmc, cost };
    },
  },

  {
    key: "deburring",
    label: "Deburring",
    aliases: ["deburring", "deburr", "debur", "de_burring", "de-burring"],
    isDeburringAdvanced: true,
    compute(i, ctx) {
      // Rate falls back to tenant config (default ₹180/hr) — treat empty
      // string / 0 as "not set" so the default kicks in.
      const defaultMhr = ctx?.machiningConfig?.deburring_mhr ?? 180;
      const mhrTyped = num(i.mhr);
      const mhr = mhrTyped > 0 ? mhrTyped : defaultMhr;
      const output = num(i.output_per_hour);
      // Efficiency defaults to 75% when the user hasn't typed anything.
      const effTyped = num(i.efficiency);
      const eff = effTyped > 0 ? effTyped : 75;
      const denom = output * (eff / 100);
      const cost = denom > 0 ? mhr / denom : 0;
      return { mhr, eff, cost };
    },
  },

  {
    key: "welding",
    label: "Welding",
    aliases: ["welding", "weld"],
    inputs: [
      { key: "rate_per_sq_ft", label: "Rate per Sq.Ft (₹)", type: "number" },
      { key: "surface_area_sq_ft", label: "Surface Area (Sq.Ft)", type: "number" },
    ],
    compute(i) {
      const cost = num(i.rate_per_sq_ft) * num(i.surface_area_sq_ft);
      return { cost };
    },
    display: (calc) => [["Welding Cost", `₹${(calc.cost || 0).toFixed(2)}`, "primary"]],
  },

  // ── Painting: Area × Rate/sq.ft. Inspection is applied by the shared
  // wrapper in CostSheetEditor so no need to include it here.
  {
    key: "painting",
    label: "Painting",
    aliases: ["painting", "paint"],
    isPaintingAdvanced: true,
    compute(i) {
      const area = num(i.area_sq_ft);
      const rate = num(i.rate_per_sq_ft);
      return { cost: area * rate };
    },
  },

  // ── Silver Plating: masking + plating (area × thickness × rate).
  // Auto-fetches the plating rate from Process Master (standardised_rate).
  // Also exposes an INFORMATIONAL consumption metric in grams:
  //     consumption_g = area × thickness × 0.001049
  // (user-directed Feb 24 2026 — 0.001049 is the mass-per-volume constant
  // for silver micron layers on the shop-floor spec sheet).
  {
    key: "silver_plating",
    label: "Silver Plating",
    aliases: ["silver_plating", "silverplating", "silver_plate"],
    isSilverPlatingAdvanced: true,
    compute(i, ctx) {
      const maskingCost = num(i.masking_area) * num(i.masking_rate);
      const platingRate = num(i.plating_rate ?? ctx?.processMeta?.standardised_rate);
      const area = num(i.plating_area);
      const thickness = num(i.plating_thickness);
      const platingCost = area * thickness * platingRate;
      const consumption_g = area * thickness * 0.001049;
      return {
        masking_cost: maskingCost,
        plating_cost: platingCost,
        plating_rate_used: platingRate,
        consumption_g,
        cost: maskingCost + platingCost,
      };
    },
  },

  // ── Generic Rate × Weight recipe used by every surface-treatment /
  // heat-treatment / finishing / packaging / dispatch process. The
  // Cost Sheet auto-fetches the process's `standardised_rate` from
  // Process Master; users can still override the rate manually.
  {
    key: "rate_x_weight",
    label: "Rate × Weight",
    aliases: [],
    isRateXWeight: true,
    compute(i, ctx) {
      const rate = num(i.rate_per_kg ?? ctx?.processMeta?.standardised_rate);
      // Weight falls back to the part's finished weight if not explicitly set.
      const explicit = num(i.weight);
      const weight = explicit > 0 ? explicit : num(ctx?.finished_weight);
      return { cost: rate * weight, rate_used: rate, weight_used: weight };
    },
  },

  {
    key: "surface_treatment",
    label: "Surface Treatment",
    aliases: ["surface_treatment"],
    optional: true,
    isDynamicList: true,     // parent checkbox + multi-treatment sub-rows
    presetTreatments: ["Hardening", "Nitriding", "Normalizing", "Tempering", "Annealing"],
    treatmentInputs: [
      { key: "rate_per_kg", label: "Rate per Kg (₹)", type: "number" },
      { key: "weight", label: "Part Weight (Kg)", type: "number", autoFrom: "finished_weight" },
    ],
    computeTreatment(t) {
      return { cost: num(t.rate_per_kg) * num(t.weight) };
    },
    compute(i) {
      const list = i.treatments || [];
      const enriched = list.map(t => ({ ...t, ...({ cost: num(t.rate_per_kg) * num(t.weight) }) }));
      return { treatments: enriched, cost: enriched.reduce((s, t) => s + (t.cost || 0), 0) };
    },
  },

  {
    key: "surface_finish",
    label: "Surface Finish",
    aliases: ["surface_finish"],
    optional: true,
    isDynamicList: true,      // free-form list of finish operations
    presetTreatments: ["Powder Coat", "Zinc Plating", "Painting", "Polishing", "Buffing", "Anodizing"],
    treatmentInputs: [
      { key: "rate_per_kg", label: "Rate per Kg (₹)", type: "number" },
      { key: "weight", label: "Part Weight (Kg)", type: "number", autoFrom: "finished_weight" },
    ],
    computeTreatment(t) {
      return { cost: num(t.rate_per_kg) * num(t.weight) };
    },
    compute(i) {
      const list = i.treatments || [];
      const enriched = list.map(t => ({ ...t, ...({ cost: num(t.rate_per_kg) * num(t.weight) }) }));
      return { treatments: enriched, cost: enriched.reduce((s, t) => s + (t.cost || 0), 0) };
    },
  },

  {
    key: "inspection",
    label: "Inspection",
    aliases: ["inspection", "insp", "quality"],
    inputs: [
      { key: "manpower_rate_per_hour", label: "Manpower Rate per Hour (₹)", type: "number" },
      { key: "output_per_hour", label: "Inspection Output per Hour (pcs)", type: "number" },
    ],
    compute(i) {
      const rate = num(i.manpower_rate_per_hour);
      const out = num(i.output_per_hour);
      const cost = out > 0 ? rate / out : 0;
      return { cost };
    },
    display: (calc) => [["Inspection Cost / pc", `₹${(calc.cost || 0).toFixed(2)}`, "primary"]],
  },

  {
    key: "packaging",
    label: "Packaging",
    aliases: ["packaging", "pack", "packing"],
    inputs: [
      { key: "rate", label: "Packaging Rate per Kg (₹)", type: "number" },
      { key: "weight", label: "Weight (Kg)", type: "number", autoFrom: "finished_weight" },
    ],
    compute: (i, ctx) => rateXWeight(i, ctx),
    display: (calc) => [["Packaging Cost", `₹${(calc.cost || 0).toFixed(2)}`, "primary"]],
  },

  {
    key: "dispatch",
    label: "Dispatch",
    aliases: ["dispatch", "shipping", "logistics", "freight"],
    inputs: [
      { key: "rate", label: "Dispatch Rate per Kg (₹)", type: "number" },
      { key: "weight", label: "Weight (Kg)", type: "number", autoFrom: "finished_weight" },
    ],
    compute: (i, ctx) => rateXWeight(i, ctx),
    display: (calc) => [["Dispatch Cost", `₹${(calc.cost || 0).toFixed(2)}`, "primary"]],
  },

  // Fallback recipe used for any unknown enabled operation. Keeps the
  // module future-proof: even a brand-new op like "Laser Cutting" gets a
  // usable Rate × Quantity section until a bespoke recipe is added.
  {
    key: "generic",
    label: "Generic Operation",
    aliases: [],
    isGeneric: true,
    inputs: [
      { key: "rate", label: "Rate (₹)", type: "number" },
      { key: "quantity", label: "Quantity / Time", type: "number" },
    ],
    compute(i) {
      return { cost: num(i.rate) * num(i.quantity) };
    },
    display: (calc) => [["Cost", `₹${(calc.cost || 0).toFixed(2)}`, "primary"]],
  },
];

// Compute Sq.Inch surface area for a part based on its RM dimensions.
// Used to prefill the Cutting section without asking the user.
export function computeSqInchArea(part) {
  const t = String(part.rm_type || "").toLowerCase();
  const mm2ToSqIn = 1 / 645.16;
  if (t === "rod") {
    const d = num(part.diameter);
    const l = num(part.length);
    // Curved surface + one end face
    const area_mm2 = Math.PI * d * l + Math.PI * (d / 2) ** 2;
    return area_mm2 * mm2ToSqIn;
  }
  if (t === "sheet") {
    const l = num(part.length);
    const w = num(part.width);
    return (l * w) * mm2ToSqIn;
  }
  if (t === "pipe") {
    const od = num(part.outer_diameter);
    const id = num(part.inner_diameter);
    const l = num(part.length);
    const area_mm2 = Math.PI * (od + id) * l;
    return area_mm2 * mm2ToSqIn;
  }
  if (t === "square") {
    const l = num(part.length);
    const w = num(part.width);
    const h = num(part.height);
    // Six-face total
    return (2 * (l * w + w * h + h * l)) * mm2ToSqIn;
  }
  return 0;
}

// Given a part's `operations_enabled` map (from Item Master), return the
// ordered list of recipe objects the Cost Sheet should render. Anything
// enabled but unrecognised falls back to a labelled `generic` recipe.
export function recipesForPart(part) {
  const enabled = part?.operations_enabled || {};
  const enabledKeys = Object.entries(enabled)
    .filter(([, v]) => !!v)
    .map(([k]) => String(k).toLowerCase());

  // Always start with Raw Material (optional via checkbox).
  const rmRecipe = COST_RECIPES.find(r => r.key === "raw_material");
  const chosen = [rmRecipe];

  // Walk enabled keys in insertion order, matching to recipes via alias.
  const seen = new Set(["raw_material"]);
  enabledKeys.forEach((k) => {
    // Skip system-only ops that don't carry a real cost.
    if (["incoming_store", "outsource", "outsource_cutting", "outsource_machining"].includes(k)) return;
    // Merge machining_p1 / machining_p2 into a single "machining" section
    const canonical = k.startsWith("machining") ? "machining" : k;
    if (seen.has(canonical)) return;
    seen.add(canonical);
    const match = COST_RECIPES.find(r => r.key === canonical || r.aliases.includes(canonical) ||
                                    r.aliases.some(a => canonical.includes(a)));
    if (match) chosen.push(match);
    else chosen.push({
      ...COST_RECIPES.find(r => r.key === "generic"),
      key: canonical,
      label: canonical.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()),
    });
  });

  // Ensure the standard "always-consider" recipes at least appear once so
  // clients can toggle them in even if the part didn't include them.
  ["surface_treatment", "surface_finish", "packaging", "dispatch"].forEach((k) => {
    if (!chosen.some(r => r.key === k)) {
      const r = COST_RECIPES.find(x => x.key === k);
      if (r) chosen.push(r);
    }
  });

  return chosen;
}

// Autofill inputs for a recipe from a part snapshot.
export function autofillInputs(recipe, part) {
  const out = {};
  if (!recipe.inputs) return out;
  recipe.inputs.forEach((f) => {
    if (f.autoFrom && part[f.autoFrom] != null) {
      out[f.key] = part[f.autoFrom];
    }
  });
  return out;
}
